import { useEffect, useRef, useState } from 'react';
import type { Distance } from '@runner/shared';
import { useUserRaces } from '../context/UserRacesContext';
import './FinishModal.css';

interface FinishModalProps {
  open: boolean;
  raceId: string;
  raceName: string;
  defaultDistance?: Distance | null;
  onClose: () => void;
}

const DISTANCE_OPTIONS: Distance[] = ['5K', '10K', 'half', 'full', '32K', 'ultra', 'trail', 'other'];

const DISTANCE_LABEL: Record<Distance, string> = {
  '5K': '5K',
  '10K': '10K',
  '32K': '32K',
  half: '하프',
  full: '풀코스',
  ultra: '울트라',
  trail: '트레일',
  other: '기타',
};

// Accepts "H:MM:SS", "MM:SS", or "M:SS" (with optional leading zeros).
// Returns total seconds, or null if invalid.
// eslint-disable-next-line react-refresh/only-export-components
export function parseFinishTime(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(':');
  if (parts.length < 2 || parts.length > 3) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0 || !Number.isInteger(n))) return null;
  let h = 0;
  let m = 0;
  let s = 0;
  if (parts.length === 3) {
    [h, m, s] = nums;
  } else {
    [m, s] = nums;
  }
  if (m > 59 || s > 59) return null;
  const total = h * 3600 + m * 60 + s;
  if (total <= 0) return null;
  return total;
}

export function FinishModal(props: FinishModalProps) {
  if (!props.open) return null;
  return <FinishModalDialog {...props} />;
}

function FinishModalDialog({
  raceId,
  raceName,
  defaultDistance,
  onClose,
}: FinishModalProps) {
  const { addFinish } = useUserRaces();
  const [timeInput, setTimeInput] = useState('');
  const [distance, setDistance] = useState<Distance>(defaultDistance ?? '10K');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const finishTimeSec = parseFinishTime(timeInput);
    if (finishTimeSec === null) {
      setError('시간 형식이 올바르지 않습니다. 예: 1:23:45 또는 45:30');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addFinish({
        raceId,
        finishTimeSec,
        distance,
        note: note.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : '기록 저장에 실패했습니다');
    }
  };

  return (
    <div
      className="finish-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="finish-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finish-modal-title"
      >
        <button
          type="button"
          className="finish-modal-close"
          aria-label="닫기"
          onClick={onClose}
        >
          ×
        </button>
        <h2 id="finish-modal-title" className="finish-modal-title">
          {raceName} 완주 기록
        </h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="finish-modal-field">
            <label htmlFor="finish-time">완주 시간</label>
            <input
              id="finish-time"
              type="text"
              inputMode="numeric"
              placeholder="H:MM:SS 또는 MM:SS"
              value={timeInput}
              onChange={(e) => setTimeInput(e.target.value)}
              aria-invalid={!!error}
              required
            />
          </div>
          <div className="finish-modal-field">
            <label htmlFor="finish-distance">거리</label>
            <select
              id="finish-distance"
              value={distance}
              onChange={(e) => setDistance(e.target.value as Distance)}
              required
            >
              {DISTANCE_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {DISTANCE_LABEL[d]}
                </option>
              ))}
            </select>
          </div>
          <div className="finish-modal-field">
            <label htmlFor="finish-note">메모 (선택)</label>
            <textarea
              id="finish-note"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {error && (
            <div className="finish-modal-error" role="alert">
              {error}
            </div>
          )}
          <div className="finish-modal-actions">
            <button
              type="button"
              className="finish-modal-btn"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="submit"
              className="finish-modal-btn finish-modal-btn-primary"
              disabled={submitting}
            >
              {submitting ? '저장 중…' : '기록 저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
